import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { setGuestSignupModalVisible, setSidebarVisible } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { selectIsGuest, selectUserSettings } from 'redux/db/reducers';
import * as routes from 'routes';
import * as S from './styles';

const NewButton = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { isMobile, designSystemsEnabled, isGuest } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			designSystemsEnabled: selectUserSettings(r.db).designSystemsEnabled,
			isGuest: selectIsGuest(r.db),
		}),
		shallowEqual
	);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isCreatingProject, setIsCreatingProject] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// Handle click outside to close dropdown
	useEffect(() => {
		const onClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};

		if (isDropdownOpen) {
			document.addEventListener('mousedown', onClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', onClickOutside);
		};
	}, [isDropdownOpen]);

	const onNewProject = () => {
		if (isGuest) {
			dispatch(setGuestSignupModalVisible(true));
			setIsDropdownOpen(false);
			return;
		}
		navigate('/');
		if (isMobile) {
			dispatch(setSidebarVisible(false));
		}
		setIsDropdownOpen(false);
	};

	const onNewDesignSystem = () => {
		if (isGuest) {
			dispatch(setGuestSignupModalVisible(true));
			setIsDropdownOpen(false);
			return;
		}
		navigate(routes.createDesignSystem());
		if (isMobile) {
			dispatch(setSidebarVisible(false));
		}
		setIsDropdownOpen(false);
	};

	const onCreateEmptyProject = async () => {
		if (isGuest) {
			dispatch(setGuestSignupModalVisible(true));
			setIsDropdownOpen(false);
			return;
		}
		if (isCreatingProject) {
			return;
		}
		setIsCreatingProject(true);
		try {
			const res = await forge.createScene.withToast()({
				title: 'My Project',
			});
			navigate(routes.scene(res.sceneId, 'My Project'));
			if (isMobile) {
				dispatch(setSidebarVisible(false));
			}
			setIsDropdownOpen(false);
		} catch (e) {
			// Error handling is done by withToast()
		} finally {
			setIsCreatingProject(false);
		}
	};

	const onToggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};

	// Mobile or legacy mode: render simple button
	if (isMobile || !designSystemsEnabled) {
		return (
			<S.NewProjectButton onClick={onNewProject} title="Create a new project">
				<Icon type={IconType.ADD} width={16} height={16} color="white" />
				New Project
			</S.NewProjectButton>
		);
	}

	// Desktop with new DS enabled: render split button with dropdown
	return (
		<S.SplitButtonContainer ref={containerRef}>
			<S.SplitButtonMain onClick={onNewProject} title="Create a new project">
				New Project
			</S.SplitButtonMain>
			<S.SplitButtonDropdown onClick={onToggleDropdown} aria-label="Show more options">
				<Icon type={IconType.FILLED_ARROW_DOWN} width={18} height={18} color="white" />
			</S.SplitButtonDropdown>
			{isDropdownOpen && (
				<S.DropdownMenu>
					<S.DropdownMenuItem onClick={onNewDesignSystem} title="Create a new design system">
						<Icon type={IconType.COMPONENT} width={14} height={14} />
						New Design System
					</S.DropdownMenuItem>
					<S.DropdownMenuItem
						onClick={onCreateEmptyProject}
						disabled={isCreatingProject}
						aria-disabled={isCreatingProject}
						title={isCreatingProject ? 'Creating project...' : 'Create a blank project'}
					>
						<Icon type={IconType.FILE} width={14} height={14} />
						{isCreatingProject ? 'Creating...' : 'New Blank Project'}
					</S.DropdownMenuItem>
				</S.DropdownMenu>
			)}
		</S.SplitButtonContainer>
	);
};

export default NewButton;
